import { useEffect, useMemo, useState } from 'react';
import { add, differenceInHours, differenceInMinutes, sub } from 'date-fns';

function parseTimeInput(input: string): { hours: number; minutes: number } {
  const normalized = input.toLowerCase().replace(/\s+/g, '');
  let hours = 0;
  let minutes = 0;

  const hourMatch = normalized.match(/(\d+)h|h(\d+)/);
  const minuteMatch = normalized.match(/(\d+)m|m(\d+)/);

  if (hourMatch) {
    hours = parseInt(hourMatch[1] || hourMatch[2], 10);
  }

  if (minuteMatch) {
    minutes = parseInt(minuteMatch[1] || minuteMatch[2], 10);
  }

  return { hours, minutes };
}

const datesToRawTimeInput = (startTime: Date, endTime: Date) => {
  const newHours = differenceInHours(endTime, startTime);
  const newMinutes = differenceInMinutes(endTime, startTime) % 60;

  if (newHours < 0 || newMinutes < 0) {
    return '';
  }

  let newRawTimeInput = '';
  if (newHours > 0) {
    newRawTimeInput += `${newHours}h `;
  }
  if (newMinutes > 0) {
    newRawTimeInput += `${newMinutes}m`;
  }
  return newRawTimeInput.trim();
};

type UseTimerOptions = {
  initialStartTime?: Date;
  initialEndTime?: Date;
};

export const useTimer = (options: UseTimerOptions = {}) => {
  const { initialStartTime, initialEndTime } = options;
  const [rawTimeInput, setRawTimeInput] = useState<string>(() => {
    if (initialStartTime && initialEndTime) {
      return datesToRawTimeInput(initialStartTime, initialEndTime);
    }
    return '';
  });
  const { hours, minutes } = useMemo(() => parseTimeInput(rawTimeInput), [rawTimeInput]);

  const [startTime, setStartTime] = useState(initialStartTime || new Date());
  const [endTime, setEndTime] = useState(initialEndTime || add(startTime, { hours, minutes }));

  useEffect(() => {
    setEndTime(add(startTime, { hours, minutes }));
  }, [startTime]);

  useEffect(() => {
    setStartTime(sub(endTime, { hours, minutes }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTimeInput]);

  const handleStartDateChange = (date: Date | null | undefined) => {
    if (!date) {
      return;
    }

    const newStartTime = new Date(startTime);
    newStartTime.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    setStartTime(newStartTime);
  };

  const handleEndDateChange = (date: Date | null | undefined) => {
    if (!date) {
      return;
    }

    const newEndTime = new Date(endTime);
    newEndTime.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());

    setEndTime(newEndTime);
    setRawTimeInput(datesToRawTimeInput(startTime, newEndTime));
  };

  const handleStartTimeSelect = (dateString: string) => {
    const newStartTime = new Date(dateString);
    setStartTime(newStartTime);
  };

  const handleEndTimeSelect = (dateString: string) => {
    const date = new Date(dateString);
    setEndTime(date);
    setRawTimeInput(datesToRawTimeInput(startTime, date));
  };

  return {
    rawTimeInput,
    setRawTimeInput,
    startTime,
    endTime,
    hours,
    minutes,
    handleStartDateChange,
    handleEndDateChange,
    handleStartTimeSelect,
    handleEndTimeSelect,
  };
};
